import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useNavigate } from 'react-router';
import { useTheme } from '@mui/material/styles';

import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/**
 * Custom hook containing callbacks and effects for data history chart
 */

import { selectDataHistoryPerDataTypesData, selectTeamHash } from 'selectors';

import { generateAllDatesWithinInterval, generateBarChartOptions } from 'utils';

import { DataHistoryPerDataTypesPerIntervalType } from 'types';

import { INTERVAL_DISPLAY, getDataType, colors, DataType } from '@constants';

import { fetchDataHistoryPerDataTypes } from 'thunk';

type IndexToIntervalMapType = {
  0: string;
  1: string;
  2: string;
  // 3: string;
};

const indexToIntervalMap = {
  0: 'day',
  1: 'week',
  2: 'month',
  3: 'year',
};

const tabsList = Object.values(INTERVAL_DISPLAY).map((interval) => {
  return {
    label: interval,
  };
});

const defaultFetchDataHistoryLimit = 12;
const defaultItem = DataType.Phase;

const useDataHistory = ({ teamId }: { teamId: number }) => {
  const {
    data: dataHistoryPerDataTypes,
    isRequesting,
    error,
  } = useAppSelector(selectDataHistoryPerDataTypesData);
  const [tabIndex, setTabIndex] = useState(0);
  const teamHash = useAppSelector(selectTeamHash);

  const selectedTeam = teamHash[teamId];

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const muiTheme = useTheme();

  // In dark mode the chart sits on a near-black MinimalCard surface, where the
  // default subtle-grey bars (rgba(0,0,0,0.15)) and Chart.js' default grey axis
  // labels nearly disappear. Use the theme accent for bars and a light tick
  // color in dark mode only; light mode keeps its existing colors unchanged.
  const isDarkMode = muiTheme.palette.mode === 'dark';
  const barColor = isDarkMode ? muiTheme.palette.primary.main : colors.lightGrey;
  const axisTextColor = isDarkMode ? muiTheme.palette.text.secondary : undefined;

  const [canFetchBefore, setCanFetchBefore] = useState(false);
  const [canFetchAfter, setCanFetchAfter] = useState(false);
  const interval = indexToIntervalMap[tabIndex as keyof IndexToIntervalMapType];

  const intervalData =
    dataHistoryPerDataTypes &&
    dataHistoryPerDataTypes[interval as keyof DataHistoryPerDataTypesPerIntervalType];

  const [showAlert, setShowAlert] = useState(false);

  const toggleAlert = () => setShowAlert(!showAlert);

  useEffect(() => {
    if (selectedTeam) {
      dispatch(
        fetchDataHistoryPerDataTypes({
          offset: 0,
          interval,
          limit: defaultFetchDataHistoryLimit,
          teamId: selectedTeam.id,
          isInitialFetch: true,
        }),
      );
    }
  }, [dispatch, navigate, selectedTeam, interval]);

  useEffect(() => {
    queueMicrotask(() => {
      if (intervalData && !intervalData.hasReachTheEnd) {
        setCanFetchBefore(true);
        setShowAlert(false);
      } else {
        setShowAlert(true);
        setCanFetchBefore(false);
      }

      if (intervalData?.offset && intervalData.offset > 0) {
        setCanFetchAfter(true);
      } else {
        setCanFetchAfter(false);
      }
    });
  }, [intervalData, intervalData?.hasReachTheEnd, intervalData?.offset]);

  const handleTabChange = (event: React.ChangeEvent<Record<string, never>>, newValue: number) => {
    setTabIndex(newValue);
  };

  const processDataHistory = (
    intervalData: any,
  ): {
    labels: Array<string>;
    createdArray: Array<number>;
    updatedArray: Array<number>;
    totalArray: Array<number>;
    globalTotal: number;
    globalMin: number;
  } => {
    const selectedDataType = dataType.value;
    const selectedInterval = indexToIntervalMap[tabIndex as keyof IndexToIntervalMapType];
    if (intervalData?.data.length === 0)
      return {
        labels: [],
        createdArray: [],
        updatedArray: [],
        totalArray: [],
        globalTotal: 0,
        globalMin: 0,
      };
    const startDate = new Date(intervalData.data[0].resInterval);
    const endDate = new Date(intervalData.data[intervalData.data.length - 1].resInterval);

    /** Generate an array of string date from startDate to endDate */
    const dateArray = generateAllDatesWithinInterval(startDate, endDate, selectedInterval);
    /** Create an accumulator with keys as string from dataArray */
    const dateAccumulator = dateArray.reduce((acc: any, dateString: string) => {
      acc[dateString] = {
        createdInInterval: 0,
        updatedInInterval: 0,
        totalInInterval: 0,
      };
      return acc;
    }, {});

    /** Interate over intervalData array and keep track of a running sum  */
    let globalTotal = 0;
    const data = intervalData.data.reduce(
      (acc: any, curr: any) => {
        const currentDate = format(toZonedTime(parseISO(curr.resInterval), 'UTC'), 'MM/dd/yyyy');
        if (curr.dataType === selectedDataType) {
          globalTotal += parseInt(curr.createdInInterval as string);
          const createdInCount = parseInt(curr.createdInInterval as string);
          const updatedInCount = parseInt(curr.updatedInInterval as string);
          acc[currentDate].createdInInterval += createdInCount;
          acc[currentDate].updatedInInterval += updatedInCount;
          // We don;t want to count 0
          acc[currentDate].totalInInterval = globalTotal;
        }

        return acc;
      },
      {
        ...dateAccumulator,
      },
    );

    /** Iterate over each interval and generate arrays that will be used for visualization */
    const dateKeys = Object.keys(data);
    let runningSum = 0; // This sum represents the sum for any missing data
    let globalMinUpdatedIn = 0;
    let globalMinCreatedIn = 0;
    let globalTotalUpdatedIn = 0;
    let globalTotalCreatedIn = 0;
    const { labels, createdArray, updatedArray, totalArray } = dateKeys.reduce(
      (
        acc: {
          labels: Array<string>;
          createdArray: Array<number>;
          updatedArray: Array<number>;
          totalArray: Array<number>;
        },
        date: string,
        index: number,
      ) => {
        acc.labels.push(date.slice(0, 5));
        const createdInCount = data[date].createdInInterval;
        const updatedInCount = data[date].updatedInInterval;
        acc.createdArray.push(createdInCount);
        acc.updatedArray.push(updatedInCount);

        // Max
        globalTotalUpdatedIn = Math.max(globalTotalUpdatedIn, updatedInCount);
        globalTotalCreatedIn = Math.max(globalTotalCreatedIn, createdInCount);
        // Min

        /** We do not want to include 0 */
        if (updatedInCount) {
          globalMinUpdatedIn = Math.min(globalMinUpdatedIn, updatedInCount);
        }

        if (createdInCount) {
          globalMinCreatedIn = Math.min(globalMinCreatedIn, createdInCount);
        }

        /** If totalInInterval of this interval is 0, meaning that it does not exist
         *  add the previous interval's totalInInterval to keep data consistentency
         */
        if (data[date].totalInInterval === 0 && index > 0) {
          acc.totalArray.push(runningSum);
        } else {
          runningSum = data[date].totalInInterval;
          acc.totalArray.push(data[date].totalInInterval);
        }
        return acc;
      },
      {
        labels: [],
        createdArray: [],
        updatedArray: [],
        totalArray: [],
      },
    );

    return {
      labels,
      createdArray,
      updatedArray,
      totalArray,
      globalMin: Math.min(globalMinCreatedIn, globalMinUpdatedIn),
      globalTotal: Math.max(globalTotalCreatedIn, globalTotalUpdatedIn),
    };
  };

  const getDataHistoryChartDataAndOptions = () => {
    if (!dataHistoryPerDataTypes)
      return {
        labels: [],
        datasets: [],
      };
    const { labels, createdArray, updatedArray, totalArray, globalTotal, globalMin } =
      processDataHistory(intervalData);
    const data = {
      labels: labels, // x-axis
      datasets: [
        {
          label: 'Created at',
          backgroundColor: barColor,
          borderColor: barColor,
          hoverBackgroundColor: barColor,
          hoverBorderColor: barColor,
          data: createdArray,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
          minBarLength: 5,
        },
        // {
        //   label: "Updated at",
        //   backgroundColor: colors.lightBlue,
        //   borderColor: colors.lightBlue,
        //   hoverBackgroundColor: colors.lightBlue,
        //   hoverBorderColor: colors.lightBlue,
        //   data: updatedArray,
        //   barPercentage: 0.5,
        //   categoryPercentage: 0.5,
        //   minBarLength: 5,
        // },
        // {
        //   label: "Total (Created at, on or before)",
        //   backgroundColor: "red",
        //   borderColor: "red",
        //   hoverBackgroundColor: "red",
        //   hoverBorderColor: "red",
        //   data: totalArray,
        //   barPercentage: 0.5,
        //   categoryPercentage: 0.5,
        // },
      ],
    };

    const chartOptions = generateBarChartOptions({
      max: globalTotal,
      min: globalMin,
      textColor: axisTextColor,
    });
    return {
      data,
      chartOptions,
    };
  };

  /** Menu logics */
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(defaultItem);

  const dataType = getDataType(selectedItem);

  const handleMenuClick = (e: any) => {
    setAnchorEl(e.target);
    setOpen(true);
  };

  const handleMenuClose = () => {
    setOpen(false);
  };

  const handleMenuItemClick = (item: DataType) => {
    setSelectedItem(item);
    setOpen(false);
  };

  /** */

  /** Pagination */

  /** Pressing > */
  const getDataIntervalBefore = () => {
    if (!selectedTeam?.id || !intervalData) return;
    dispatch(
      fetchDataHistoryPerDataTypes({
        offset: intervalData.offset + 1,
        interval,
        limit: defaultFetchDataHistoryLimit,
        teamId: selectedTeam.id,
        isInitialFetch: false,
      }),
    );
  };

  /** Pressing < */
  const getDataIntervalAfter = () => {
    if (!selectedTeam?.id || !intervalData) return;
    dispatch(
      fetchDataHistoryPerDataTypes({
        offset: intervalData.offset - 1,
        interval,
        limit: defaultFetchDataHistoryLimit,
        teamId: selectedTeam.id,
        isInitialFetch: false,
      }),
    );
  };

  return {
    dataHistoryTabsList: tabsList,
    dataHistoryTabIndex: tabIndex,
    dataHistoryOnHandleTabChange: handleTabChange,
    dataHistorydata: getDataHistoryChartDataAndOptions(),
    dataHistoryMenuAnchor: anchorEl,
    isDataTypesMenuOpen: open,
    selectedDataType: dataType,
    defaultDataHistoryMenuText: 'Selected a data type',
    dataTypesMenuOnClose: handleMenuClose,
    dataTypesMenuOnClick: handleMenuClick,
    dataTypesMenuOnItemClick: handleMenuItemClick,
    getDataIntervalBefore,
    getDataIntervalAfter,
    canFetchAfter,
    canFetchBefore,
    isDataHistoryLoading: isRequesting,
    dataHistoryError: error,
    showAlert,
    toggleAlert,
  };
};

export default useDataHistory;
